import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import NotificationEventAccountPicker from "./NotificationEventAccountPicker";

export interface NotificationEventPreferenceRowProps {
  eventKey: string;
  title: string;
  inApp: boolean;
  email: boolean;
  showEmailToggle: boolean;
  mode: "all" | "selected";
  selectedCodes: string[];
  hasScopeError: boolean;
  accountsLoading: boolean;
  accountCodeOptions: string[];
  onToggleInApp: () => void;
  onToggleEmail: () => void;
  onScopeModeChange: (mode: "all" | "selected") => void;
  onScopeAccountsChange: (codes: string[]) => void;
}

export function NotificationEventPreferenceRow({
  eventKey,
  title,
  inApp,
  email,
  showEmailToggle,
  mode,
  selectedCodes,
  hasScopeError,
  accountsLoading,
  accountCodeOptions,
  onToggleInApp,
  onToggleEmail,
  onScopeModeChange,
  onScopeAccountsChange,
}: NotificationEventPreferenceRowProps): React.ReactElement {
  return (
    <div
      data-notification-event-row=""
      data-event-key={eventKey}
      className={NOTIFICATION_EVENT_GRID_CLASS}
    >
      <Field className="min-w-0 gap-1.5" data-invalid={hasScopeError || undefined}>
        <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{title}</FieldLabel>
        {/* Account scope as a compact two-way pill. */}
        <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label={`${title} accounts`}>
          {(["all", "selected"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => onScopeModeChange(m)}
              className={cn(
                "!rounded-full !border !border-solid !px-2 !py-px !text-[11px] !leading-4 transition-colors",
                mode === m
                  ? "!border-[color:var(--brand-tropical-surf)] !bg-[color:var(--brand-tropical-surf)]/10 !text-[color:var(--brand-tropical-surf)]"
                  : "!border-border !bg-transparent !text-muted-foreground hover:!text-foreground"
              )}
            >
              {m === "all" ? "All accounts" : `Selected accounts${mode === "selected" && selectedCodes.length ? ` (${selectedCodes.length})` : ""}`}
            </button>
          ))}
        </div>

          {mode === "selected" ? (
            <div className="mt-1 w-fit max-w-full">
              {accountsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden />
                  <span className={BROKER_FIELD_DESC_CLASS}>Loading accounts...</span>
                </div>
              ) : accountCodeOptions.length === 0 ? (
                <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                  No broker accounts available yet. Connect or sync an account first.
                </FieldDescription>
              ) : (
                <>
                  <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                    Choose which accounts should receive this notification.
                  </FieldDescription>
                  <NotificationEventAccountPicker
                    accountCodeOptions={accountCodeOptions}
                    selectedCodes={selectedCodes}
                    onChange={onScopeAccountsChange}
                  />
                </>
              )}
            </div>
          ) : null}

          {hasScopeError ? (
            <FieldDescription className="text-xs text-destructive">
              Select at least one account for selected scope.
            </FieldDescription>
          ) : null}
      </Field>
      <span className="flex justify-center pt-0.5">
        <Switch checked={inApp} onCheckedChange={onToggleInApp} aria-label={`${title} in-app`} />
      </span>
      <span className="flex justify-center pt-0.5">
        {showEmailToggle ? (
          <Switch checked={email} onCheckedChange={onToggleEmail} aria-label={`${title} email`} />
        ) : (
          <span className={BROKER_FIELD_DESC_CLASS} title="Not sent by email">—</span>
        )}
      </span>
    </div>
  );
}

/** Title + scope on the left, one centred column per channel (shared with the header row). */
export const NOTIFICATION_EVENT_GRID_CLASS =
  "grid grid-cols-[minmax(0,1fr)_64px_64px] items-start gap-3 px-4 py-3.5";

/** Column headings above the event rows: "In-app" / "Email". */
export function NotificationEventListHeader(): React.ReactElement {
  return (
    <div
      className={cn(NOTIFICATION_EVENT_GRID_CLASS, "py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground")}
    >
      <span>Event</span>
      <span className="text-center">In-app</span>
      <span className="text-center">Email</span>
    </div>
  );
}
